import { __read } from "tslib";
/* eslint-disable @typescript-eslint/no-empty-function */
import { arrayMove } from '@dnd-kit/sortable';
import { useLazyLoadQuery } from '@nova/react';
import * as React from 'react';
import { controlModelQuery, dataQuery } from './AppBar.types';
/**
 * Default function implementation for functions that should be overridden
 */
var NotImplemented = function () {
    throw new Error('Function not implemented. Use the useAppBarContext hook to utilize AppBar context methods');
};
/**
 * Initial context stub object
 */
export var AppBarInitialContext = {
    overflowIndex: undefined,
    maxLockedIndex: undefined,
    maxPinnedIndex: undefined,
    appBarData: undefined,
    hoistedItem: undefined,
    lockedItems: undefined,
    pinnedItems: undefined,
    overflowItems: undefined,
    anchoredItems: undefined,
    allAppBarItems: undefined,
    setLockedItems: NotImplemented,
    setPinnedItems: NotImplemented,
    setHoistedItem: NotImplemented,
    setOverflowIndex: NotImplemented,
    setAnchoredItems: NotImplemented,
    movePinnedItem: NotImplemented,
};
/**
 * Context object containing state data and methods to act on that data
 */
var AppBarContext = React.createContext(AppBarInitialContext);
/**
 * Provides an AppBarContext implementation containing state for visible, hoisted and flyout AppBar items
 */
var AppBarContextProvider = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k;
    var children = _a.children;
    var controlModelDataQuery = useLazyLoadQuery(controlModelQuery, {});
    var appBarDataQuery = useLazyLoadQuery(dataQuery, {});
    var _l = __read(React.useState(), 2), overflowIndex = _l[0], setOverflowIndex = _l[1];
    var _m = __read(React.useState(), 2), selectedItem = _m[0], setSelectedItem = _m[1];
    var _o = __read(React.useState([]), 2), lockedItems = _o[0], setLockedItems = _o[1];
    var _p = __read(React.useState([]), 2), pinnedItems = _p[0], setPinnedItems = _p[1];
    var _q = __read(React.useState(), 2), hoistedItem = _q[0], setHoistedItem = _q[1];
    var _r = __read(React.useState([]), 2), overflowItems = _r[0], setOverflowItems = _r[1];
    var _s = __read(React.useState([]), 2), anchoredItems = _s[0], setAnchoredItems = _s[1];
    var _t = __read(React.useState([]), 2), allAppBarItems = _t[0], setAllAppBarItems = _t[1];
    var _u = React.useMemo(function () {
        var _a, _b, _c;
        return (((_c = (_b = (_a = appBarDataQuery.data) === null || _a === void 0 ? void 0 : _a.m365Apps) === null || _b === void 0 ? void 0 : _b.edges) === null || _c === void 0 ? void 0 : _c.reduce(function (_a, edge) {
            var _b, _c;
            var maxLockedIndex = _a.maxLockedIndex, maxPinnedIndex = _a.maxPinnedIndex;
            if (((_b = edge === null || edge === void 0 ? void 0 : edge.node) === null || _b === void 0 ? void 0 : _b.lockedIndex) != null &&
                edge.node.lockedIndex > maxLockedIndex) {
                maxLockedIndex = edge.node.lockedIndex;
            }
            if (((_c = edge === null || edge === void 0 ? void 0 : edge.node) === null || _c === void 0 ? void 0 : _c.pinnedIndex) != null &&
                edge.node.pinnedIndex > maxPinnedIndex) {
                maxPinnedIndex = edge.node.pinnedIndex;
            }
            return { maxLockedIndex: maxLockedIndex, maxPinnedIndex: maxPinnedIndex };
        }, { maxLockedIndex: -1, maxPinnedIndex: -1 })) || { maxLockedIndex: -1, maxPinnedIndex: -1 });
    }, [appBarDataQuery.data]), maxLockedIndex = _u.maxLockedIndex, maxPinnedIndex = _u.maxPinnedIndex;
    /**
     * Wraps a GQL edge in an AppBarItem
     * @param item Edge object to wrap
     * @returns AppBarItem with contained edge data
     */
    var wrapNewItem = function (item) {
        var newItem = {
            data: {
                node: Object.assign({}, item.node),
            },
            elementRef: React.createRef(),
            itemRef: React.createRef(),
        };
        if (newItem.data.node.isInUse) {
            setSelectedItem(newItem);
        }
        return newItem;
    };
    /**
     * Sorted tile data specified by GQL AppBar data.
     * Sort order will be:  locked tiles > pinned tiles > rest of the tiles
     */
    var appBarData = React.useMemo(function () {
        if (appBarDataQuery.error) {
            throw appBarDataQuery.error;
        }
        else if (!(appBarDataQuery === null || appBarDataQuery === void 0 ? void 0 : appBarDataQuery.data)) {
            return undefined;
        }
        return appBarDataQuery.data;
    }, [appBarDataQuery.data, appBarDataQuery.error]);
    /**
     * All apps as defined in GQL
     */
    var allApps = React.useMemo(function () {
        var _a;
        var getEffectiveIndex = function (edge) {
            var _a, _b;
            if (((_a = edge === null || edge === void 0 ? void 0 : edge.node) === null || _a === void 0 ? void 0 : _a.lockedIndex) != null && edge.node.lockedIndex >= 0) {
                // If the node has a locked index specified, its value is its effective index
                return edge.node.lockedIndex;
            }
            else if (((_b = edge === null || edge === void 0 ? void 0 : edge.node) === null || _b === void 0 ? void 0 : _b.pinnedIndex) != null &&
                edge.node.pinnedIndex >= 0) {
                // If the node is pinned, its value incremented by the max lock index is the effective index
                return maxLockedIndex + 1 + edge.node.pinnedIndex;
            }
            else {
                // All other nodes go to the end
                return Number.MAX_VALUE;
            }
        };
        return (_a = appBarData === null || appBarData === void 0 ? void 0 : appBarData.m365Apps) === null || _a === void 0 ? void 0 : _a.edges.slice().sort(function (edge1, edge2) {
            var _a, _b, _c, _d, _e;
            // For sorting purposes, nodes where effective index is not >= 0 are moved to the back of the array
            var effectiveIndex1 = getEffectiveIndex(edge1);
            var effectiveIndex2 = getEffectiveIndex(edge2);
            // Sort by effective index first, then by name based on the prop value
            var shouldOverrideNameSorting = (_d = (_c = (_b = (_a = controlModelDataQuery === null || controlModelDataQuery === void 0 ? void 0 : controlModelDataQuery.data) === null || _a === void 0 ? void 0 : _a.views) === null || _b === void 0 ? void 0 : _b.appBarControl) === null || _c === void 0 ? void 0 : _c.tileBehavior) === null || _d === void 0 ? void 0 : _d.overrideAppBarDefaultSort;
            if (shouldOverrideNameSorting) {
                return effectiveIndex1 - effectiveIndex2;
            }
            else {
                return (effectiveIndex1 - effectiveIndex2 ||
                    ((_e = edge1.node.name) === null || _e === void 0 ? void 0 : _e.localeCompare(edge2.node.name)));
            }
        });
    }, [
        (_b = appBarData === null || appBarData === void 0 ? void 0 : appBarData.m365Apps) === null || _b === void 0 ? void 0 : _b.edges,
        maxLockedIndex,
        (_f = (_e = (_d = (_c = controlModelDataQuery === null || controlModelDataQuery === void 0 ? void 0 : controlModelDataQuery.data) === null || _c === void 0 ? void 0 : _c.views) === null || _d === void 0 ? void 0 : _d.appBarControl) === null || _e === void 0 ? void 0 : _e.tileBehavior) === null || _f === void 0 ? void 0 : _f.overrideAppBarDefaultSort,
    ]);
    /**
     * Visible tiles defined as allApps split at the overflow index
     */
    var lockedTiles = React.useMemo(function () {
        return allApps === null || allApps === void 0 ? void 0 : allApps.filter(function (tile) {
            // Only show tiles with a lockedIndex or pinnedIndex in the AppBar
            return tile.node.lockedIndex != null && tile.node.lockedIndex >= 0;
        });
    }, [allApps]);
    /**
     * Visible tiles defined as allApps split at the overflow index
     */
    var pinnedTiles = React.useMemo(function () {
        var _a, _b, _c, _d;
        var includeLockedTiles = !((_d = (_c = (_b = (_a = controlModelDataQuery === null || controlModelDataQuery === void 0 ? void 0 : controlModelDataQuery.data) === null || _a === void 0 ? void 0 : _a.views) === null || _b === void 0 ? void 0 : _b.appBarControl) === null || _c === void 0 ? void 0 : _c.tileBehavior) === null || _d === void 0 ? void 0 : _d.shouldRemoveLockedAppsFromPinnedApps);
        return allApps === null || allApps === void 0 ? void 0 : allApps.filter(function (tile) {
            var includeTileWithPinnedIndex = tile.node.pinnedIndex != null && tile.node.pinnedIndex >= 0;
            var excludeTileWithLockedIndex = tile.node.lockedIndex == null ||
                tile.node.lockedIndex === undefined ||
                tile.node.lockedIndex < 0;
            // if includeLockedTiles is true, include all tiles with pinnedIndex irrespective of lockedIndex
            // if includeLockedTiles is false, include only tiles with pinnedIndex and no lockedIndex
            return (includeTileWithPinnedIndex &&
                (includeLockedTiles || excludeTileWithLockedIndex));
        });
    }, [
        allApps,
        (_k = (_j = (_h = (_g = controlModelDataQuery === null || controlModelDataQuery === void 0 ? void 0 : controlModelDataQuery.data) === null || _g === void 0 ? void 0 : _g.views) === null || _h === void 0 ? void 0 : _h.appBarControl) === null || _j === void 0 ? void 0 : _j.tileBehavior) === null || _k === void 0 ? void 0 : _k.shouldRemoveLockedAppsFromPinnedApps,
    ]);
    /**
     * Visible tiles defined as allApps that are anchored to the bottom of the App Bar.
     */
    var anchoredTiles = React.useMemo(function () {
        return allApps === null || allApps === void 0 ? void 0 : allApps.filter(function (tile) {
            return (tile.node.anchoredIndex != null &&
                tile.node.anchoredIndex >= 0);
        }).sort(function (edge1, edge2) {
            var edge1AnchoredIndex = edge1.node.anchoredIndex || 0;
            var edge2AnchoredIndex = edge2.node.anchoredIndex || 0;
            return edge1AnchoredIndex - edge2AnchoredIndex;
        });
    }, [allApps]);
    /**
     * Remaining tiles which are not visible and displayed in overflow
     */
    var overflowTiles = React.useMemo(function () {
        if (overflowIndex != null) {
            return allApps === null || allApps === void 0 ? void 0 : allApps.slice(overflowIndex).filter(function (tile) {
                return (!tile.node.isInUse && tile.node.anchoredIndex == null);
            });
        }
        else {
            return [];
        }
    }, [allApps, overflowIndex]);
    /**
     * Returns the index in allApps that is currently selected by the user
     */
    var selectedTileIndex = React.useMemo(function () {
        var _a;
        var selectedIndex = (_a = allApps === null || allApps === void 0 ? void 0 : allApps.findIndex(function (elem) {
            return !!elem.node.isInUse;
        })) !== null && _a !== void 0 ? _a : -1;
        if (selectedItem && selectedIndex === -1) {
            setSelectedItem(undefined);
        }
        return selectedIndex;
    }, [allApps, selectedItem]);
    /**
     * Tile in overflow that is currently in use and hoisted to the AppBar
     */
    React.useEffect(function () {
        if (!allApps) {
            return;
        }
        if (overflowIndex !== undefined &&
            selectedTileIndex !== undefined &&
            selectedTileIndex >= overflowIndex &&
            allApps[selectedTileIndex].node.anchoredIndex == null) {
            setHoistedItem(wrapNewItem(allApps[selectedTileIndex]));
        }
        else {
            setHoistedItem(undefined);
        }
    }, [allApps, overflowIndex, selectedTileIndex]);
    var movePinnedItem = React.useCallback(function (id, index) {
        var newPinnedItems = [];
        pinnedItems.map(function (item) {
            newPinnedItems.push(item);
        });
        if (hoistedItem && id === hoistedItem.data.node.id) {
            if (hoistedItem.data.node.pinnedIndex === undefined) {
                newPinnedItems.push(hoistedItem);
            }
            setHoistedItem(undefined);
        }
        var oldIndex = newPinnedItems.findIndex(function (item) {
            return item.data.node.id === id;
        });
        if (oldIndex === -1) {
            return;
        }
        var newOrder = arrayMove(newPinnedItems, oldIndex, index);
        setPinnedItems(newOrder);
    }, [hoistedItem, pinnedItems]);
    /**
     * Effect that updates items in state based on parsed GQL data
     * wrapping each one in an AppBarItem prior to insertion
     */
    React.useEffect(function () {
        if (!allApps) {
            return;
        }
        var edgeToItemArray = function (items) {
            if (!items) {
                return [];
            }
            return items.map(wrapNewItem);
        };
        setAllAppBarItems(edgeToItemArray(allApps));
        setLockedItems(edgeToItemArray(lockedTiles));
        setPinnedItems(edgeToItemArray(pinnedTiles));
        setOverflowItems(edgeToItemArray(overflowTiles));
        setAnchoredItems(edgeToItemArray(anchoredTiles));
    }, [overflowTiles, lockedTiles, pinnedTiles, anchoredTiles, allApps]);
    return (React.createElement(AppBarContext.Provider, { value: {
            appBarData: appBarData,
            overflowIndex: overflowIndex,
            maxLockedIndex: maxLockedIndex,
            maxPinnedIndex: maxPinnedIndex,
            setOverflowIndex: setOverflowIndex,
            lockedItems: lockedItems,
            setLockedItems: setLockedItems,
            pinnedItems: pinnedItems,
            setPinnedItems: setPinnedItems,
            movePinnedItem: movePinnedItem,
            hoistedItem: hoistedItem,
            setHoistedItem: setHoistedItem,
            overflowItems: overflowItems,
            selectedItem: selectedItem,
            anchoredItems: anchoredItems,
            setAnchoredItems: setAnchoredItems,
            allAppBarItems: allAppBarItems,
        } }, children));
};
export { AppBarContext, AppBarContextProvider };
//# sourceMappingURL=AppBarContext.js.map